import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Lock, RefreshCw, Star, ChevronDown, ChevronRight, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { translateWorldTitle, translateWorldSubtitle, translateLevelName } from "../lib/journeyLabels";
import JourneyPath from "../components/JourneyPath";
import { cacheJourneyCatalog } from "../lib/offlineQueue";

// Each world (milestone) gets its own on-theme backdrop photo.
const WORLD_BG = {
  couch_to_active: "https://images.unsplash.com/photo-1765432424043-35be7717f0de?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  "5k_training": "https://images.unsplash.com/photo-1502904550040-7534597429ae?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  marathon_prep: "https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  boot_camp: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
  iron_will: "https://images.unsplash.com/photo-1548690312-e3b507d8c110?crop=entropy&cs=srgb&fm=jpg&w=1200&q=60",
};

export default function Journey() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const focus = location.state?.focus; // { world_id, level_id } from Continue
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [focusedWorld, setFocusedWorld] = useState(null);
  const worldRefs = useRef({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get("/journey");
      setData(data);
      // Cache level→layout metadata so boards can be generated offline.
      cacheJourneyCatalog(data.worlds);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("journey.couldnt_load"));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // When arriving from a "Continue" action, expand + scroll to the focused
  // world and briefly highlight it so the player sees exactly where they are.
  useEffect(() => {
    if (!data || !focus?.world_id) return;
    setExpanded((e) => ({ ...e, [focus.world_id]: true }));
    setFocusedWorld(focus.world_id);
    const scrollT = setTimeout(() => {
      // Prefer scrolling to the SPECIFIC next/active level node so the player
      // lands on the step they're about to play — not the middle of the world
      // card (which showed completed steps). Falls back to the world card.
      const node = focus.level_id
        ? document.querySelector(`[data-journey-node="${focus.level_id}"]`)
        : null;
      const el = node || worldRefs.current[focus.world_id];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    const clearT = setTimeout(() => setFocusedWorld(null), 3200);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
  }, [data, focus?.world_id, focus?.level_id]);

  if (error) {
    return (
      <div data-testid="journey-error" className="p-8 rounded-3xl bg-white border border-[#E6E2D8] text-center max-w-xl mx-auto">
        <p className="text-sm text-[#A0454E] mb-3">{t("journey.couldnt_load")}</p>
        <p className="text-xs text-[#2C332A]/60 mb-4">{error}</p>
        <Button onClick={load} variant="outline" className="rounded-full" data-testid="journey-retry">
          <RefreshCw size={14} strokeWidth={1.6} className="mr-1.5" />
          {t("journey.try_again")}
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="journey-skeleton" className="space-y-6 animate-pulse max-w-xl mx-auto">
        <div className="h-6 w-32 rounded bg-[#E6E2D8]" />
        <div className="h-10 w-3/4 rounded bg-[#E6E2D8]" />
        <div className="h-64 rounded-3xl bg-[#EFEBE0]" />
      </div>
    );
  }

  const earnedStars = data.worlds.reduce((s, w) => s + w.levels.reduce((a, l) => a + (l.stars || 0), 0), 0);
  const maxStars = data.total_levels * 3;

  const buildNodes = (w) => {
    const total = w.levels.length;
    return w.levels.map((l, i) => {
      const isLast = i === total - 1;
      const state = l.completed ? "completed" : l.unlocked ? "active" : "locked";
      return {
        key: l.level_id,
        kind: isLast ? "peak" : "level",
        label: isLast ? t("dashboard.peak_goal", "Final Goal") : translateLevelName(l, t),
        sub: isLast ? translateLevelName(l, t) : null,
        state,
        stars: l.stars || 0,
        calories: l.calories || 0,
        bestTimeSec: l.best_time_sec ?? null,
        onClick: l.unlocked ? () => navigate(`/play/${w.world_id}/${l.level_id}`) : undefined,
      };
    });
  };

  return (
    <div className="space-y-5 max-w-xl mx-auto pb-8">
      <div className="text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8AA48F] mb-1 font-bold">{t("journey.your_journey")}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#2C332A]">
          {t("journey.headline")}
        </h1>
        <div className="mt-3 flex items-center justify-center gap-4">
          <span className="text-sm text-[#2C332A]/70" data-testid="journey-levels-count">
            {t("daily_goal.steps_complete", { done: data.completed_levels, total: data.total_levels })}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#B07B3C]" data-testid="journey-total-stars">
            <Star size={14} strokeWidth={1.5} fill="#E8A365" className="text-[#E8A365]" />
            {earnedStars}/{maxStars}
          </span>
        </div>
      </div>

      {data.worlds.map((w) => {
        const nodes = buildNodes(w);
        const activeIndex = nodes.findIndex((n) => n.state === "active");
        const bg = WORLD_BG[w.world_id];
        const total = w.levels.length;
        const done = w.levels.filter((l) => l.completed).length;
        const wStars = w.levels.reduce((a, l) => a + (l.stars || 0), 0);
        const isActive = activeIndex >= 0;
        const isFull = done === total;

        if (!w.unlocked) {
          return (
            <div
              key={w.world_id}
              data-testid={`world-${w.world_id}`}
              className="relative rounded-3xl overflow-hidden border border-[#E6E2D8] h-28"
            >
              <div className="absolute inset-0" style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(1)" }} />
              <div className="absolute inset-0 bg-[#2C332A]/70" />
              <div className="relative h-full flex items-center justify-between px-5">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">{translateWorldSubtitle(w, t)}</p>
                  <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-semibold text-white/85 truncate">{translateWorldTitle(w, t)}</h2>
                </div>
                <div className="flex items-center gap-1.5 text-white/70 text-xs shrink-0 bg-black/25 rounded-full px-3 py-1.5">
                  <Lock size={13} strokeWidth={1.8} />
                  {t("journey.unlock_after", { n: w.unlock_level_required })}
                </div>
              </div>
            </div>
          );
        }

        const open = expanded[w.world_id] !== undefined ? expanded[w.world_id] : (isActive || !isFull);
        const toggle = () => setExpanded((e) => ({ ...e, [w.world_id]: !open }));

        return (
          <motion.div
            key={w.world_id}
            ref={(el) => { worldRefs.current[w.world_id] = el; }}
            data-testid={`world-${w.world_id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-3xl overflow-hidden border bg-white transition-all duration-500 ${
              focusedWorld === w.world_id
                ? "border-[#E8A365] ring-4 ring-[#E8A365]/40 scale-[1.01]"
                : "border-[#E6E2D8]"
            }`}
          >
            <button
              type="button"
              data-testid={`world-toggle-${w.world_id}`}
              onClick={toggle}
              className="relative w-full h-32 text-left"
            >
              <div className="absolute inset-0" style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(44,51,42,0.86) 0%, rgba(44,51,42,0.5) 58%, rgba(44,51,42,0.28) 100%)" }} />
              {/* warm gold trail accent along the bottom edge */}
              <div className="absolute inset-x-0 bottom-0 h-[3px]" style={{ background: "linear-gradient(90deg,#E0A15C, rgba(224,161,92,0))" }} />
              <div className="relative h-full flex items-center justify-between px-5">
                <div className="min-w-0 flex-1 pr-3">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-bold">{translateWorldSubtitle(w, t)}</p>
                  <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-semibold text-white truncate">{translateWorldTitle(w, t)}</h2>
                  <div className="mt-1.5 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-white/90">
                      {isFull ? <Check size={13} strokeWidth={2.4} /> : null}
                      {done}/{total}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#F0C98B]">
                      <Star size={12} strokeWidth={1.5} fill="#E8A365" className="text-[#E8A365]" />
                      {wStars}/{total * 3}
                    </span>
                  </div>
                  {/* trail progress bar */}
                  <div className="mt-2 h-1.5 w-40 max-w-full rounded-full bg-white/20 overflow-hidden" data-testid={`world-progress-${w.world_id}`}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg,#F0B072,#D9924E)" }}
                      initial={{ width: 0 }}
                      animate={{ width: `${total ? (done / total) * 100 : 0}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                  </div>
                </div>
                <div className="shrink-0 w-9 h-9 rounded-full bg-white/15 grid place-items-center text-white">
                  {open ? <ChevronDown size={20} strokeWidth={2} /> : <ChevronRight size={20} strokeWidth={2} />}
                </div>
              </div>
            </button>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-2">
                    <JourneyPath nodes={nodes} activeIndex={activeIndex} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
